import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AccountActivityItem } from '../../utils/accountActivity';
import { formatSingaporeDateTime } from '../../utils/dateTime';

interface AccountActivityFeedProps {
  items: AccountActivityItem[];
  /** When set, only the first N items show until the user taps View all. */
  previewLimit?: number;
}

const TONE_LABEL: Record<AccountActivityItem['tone'], string> = {
  project: 'Project',
  contact: 'Contact',
  task: 'Task',
  update: 'Update',
  document: 'File',
  minutes: 'Minutes',
  transfer: 'Transfer',
  profile: 'Profile',
};

export function AccountActivityFeed({ items, previewLimit }: AccountActivityFeedProps) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) {
    return <p className="account-timeline-empty">No CRM activity recorded yet.</p>;
  }

  const limit = previewLimit && previewLimit > 0 ? previewLimit : undefined;
  const showPreview = limit !== undefined && !expanded && items.length > limit;
  const visible = showPreview ? items.slice(0, limit) : items;
  const hiddenCount = showPreview ? items.length - limit : 0;

  return (
    <>
      <ol className="account-timeline">
        {visible.map((item) => (
          <li key={item.id} className={`account-timeline__item account-timeline__item--${item.tone}`}>
            <div className="account-timeline__dot" aria-hidden />
            <article className="account-timeline__card">
              <header className="account-timeline__card-head">
                <span className="account-timeline__badge">{TONE_LABEL[item.tone]}</span>
                <time dateTime={item.date}>{formatSingaporeDateTime(item.date)}</time>
              </header>
              {item.href ? (
                <Link to={item.href} className="account-timeline__title">
                  {item.title}
                </Link>
              ) : (
                <strong className="account-timeline__title">{item.title}</strong>
              )}
              <p className="account-timeline__detail">{item.detail}</p>
            </article>
          </li>
        ))}
      </ol>
      {hiddenCount > 0 ? (
        <button type="button" className="account-side-view-all" onClick={() => setExpanded(true)}>
          View all ({hiddenCount})
        </button>
      ) : null}
    </>
  );
}
